import { v7 } from 'uuid';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { findSelectedArticle } from '../business/articleHelper';
import {
  type FooterInfos,
  InterventionType,
  type InterventionUpdateType,
  type RealizationSite,
  type InterventionState,
  type InterventionFiles,
  type PatchInterventionProduct,
} from '../apis/interventions/intervention.type';
import { type MasterDataStore } from '../apis/masterdata/masterdata.type';
import { type Entity, type InterventionNature } from '../apis/ilink.type';
import { InterventionStatus } from '../business/interventionButtonMenu.const';
import { EntityType } from '../apis/entity.type';
import { type BusinessType } from '../apis/startup/startup.type';

export const shippingContainers = {
  box: 1,
  magnum: 3,
  roll: 4,
  pallet: 20,
};

interface DisableState {
  actionButton: boolean;
}

interface ModalsDisplay {
  isDeletionModalOpen: boolean;
  isValidationModalOpen: boolean;
  isShippingModalOpen: boolean;
  isReceptionModalOpen: boolean;
}

interface MissingFields {
  purchaseDate: boolean;
}

export type TraceabilitySource = 'no_source' | 'from_purchases' | 'from_modal' | 'from_intervention' | 'from_homepage' | 'from_booking';

interface InterventionStoreState {
  isToDelete?: boolean;
  interventionType: InterventionType;
  businessType: BusinessType | null;
  natures: InterventionNature[];
  storeComment: string;
  workshopComment: string;
  files: InterventionFiles;
  footer: FooterInfos;
  creationSite?: MasterDataStore;
  realizationSite?: RealizationSite;
  isWarranty?: boolean;
  isBatteryChecked: boolean | null;
  interventionRef?: string;
  reload: boolean;
  fromTraceability: boolean;
  traceabilitySource: TraceabilitySource;
  maxOgeaAmount?: number;
  currency: string;
  disable: DisableState;
  modalsDisplay: ModalsDisplay;
  missingFields: MissingFields;
  depositedProductId?: number;
  depositedProduct?: PatchInterventionProduct;
  bonusRepairCode: string | null;
}

interface AddOrUpdateInterventionNaturesPayload {
  interventionNatures: InterventionNature[];
}

const findSamePogoEntity = (entity: Entity, entityToCompare: Entity, isServiceEntity: boolean) => {
  const articleId = entity.articles.find(findSelectedArticle)?.articleId;
  const articleIdToCompare = entityToCompare.articles.find(findSelectedArticle)?.articleId;

  if (!isServiceEntity) {
    return articleIdToCompare === articleId;
  }

  if (entityToCompare.modelCode !== entity.modelCode) {
    return false;
  }

  const gridValueId = entity.articles.find(findSelectedArticle)?.gridValueId;
  const gridValueIdToCompare = entityToCompare.articles.find(findSelectedArticle)?.gridValueId;

  return gridValueIdToCompare === gridValueId;
};

const updateEntity = (entity: Entity, stateEntities: Entity[], isServiceEntity = false) =>
  stateEntities
    .map(stateEntity => (stateEntity.id === entity.id ? { ...stateEntity, ...entity } : stateEntity))
    .filter(stateEntity => stateEntity.quantity > 0)
    .reduce((updatedEntities: Entity[], currentEntity) => {
      const index = updatedEntities.findIndex(stateEntity => findSamePogoEntity(stateEntity, currentEntity, isServiceEntity));
      if (index === -1) {
        updatedEntities.push(currentEntity);
      } else {
        updatedEntities[index] = { ...updatedEntities[index], quantity: updatedEntities[index].quantity + currentEntity.quantity };
      }
      return updatedEntities;
    }, []);

export const addEntities = (entities: Entity[], stateEntities: Entity[], isServiceEntity = false) => {
  const updatedEntities = [...stateEntities];
  entities.forEach(entity => {
    const index = updatedEntities.findIndex(stateEntity => findSamePogoEntity(stateEntity, entity, isServiceEntity));
    if (index === -1) {
      const newEntity = isServiceEntity ? entity : { ...entity, orderInProgress: false };
      updatedEntities.push({ ...newEntity, id: newEntity.id || v7() });
    } else {
      updatedEntities[index] = { ...updatedEntities[index], quantity: updatedEntities[index].quantity + entity.quantity };
    }
  });
  return updatedEntities;
};

const addNatureEntities = (newNature: InterventionNature, natures: InterventionNature[]) =>
  [...natures].map(nature =>
    nature.natureId === newNature.natureId
      ? {
          ...nature,
          services: addEntities(newNature.services, nature.services, true),
          spareParts: addEntities(newNature.spareParts, nature.spareParts),
        }
      : nature
  );

export const initialState: InterventionStoreState = {
  isToDelete: false,
  interventionType: InterventionType.standard,
  businessType: null,
  natures: [],
  storeComment: '',
  workshopComment: '',
  files: {
    list: null,
    names: [],
  },
  interventionRef: '',
  footer: {
    pledgeDate: new Date(),
    state: {
      stateId: InterventionStatus.notStarted,
      stateLabel: '',
    },
  },
  isBatteryChecked: null,
  reload: false,
  fromTraceability: false,
  traceabilitySource: 'no_source',
  currency: '',
  disable: {
    actionButton: false,
  },
  modalsDisplay: {
    isDeletionModalOpen: false,
    isValidationModalOpen: false,
    isShippingModalOpen: false,
    isReceptionModalOpen: false,
  },
  missingFields: {
    purchaseDate: false,
  },
  depositedProduct: {},
  bonusRepairCode: null,
};

const updatePecEntity = (
  natures: InterventionNature[],
  natureId: number,
  entityTypes: EntityType,
  entity: Entity,
  isService = false
): InterventionNature[] =>
  natures
    .map(nature =>
      nature.natureId === natureId
        ? {
            ...nature,
            [entityTypes]: updateEntity(entity, nature[entityTypes], isService),
          }
        : nature
    )
    .filter(nature => !!nature.services.length || !!nature.spareParts.length);

const interventionSlice = createSlice({
  name: 'intervention',
  initialState,
  extraReducers: builder => builder.addCase('resetStore', () => initialState),
  reducers: {
    initInterventionState: state => {
      state.footer = { ...state.footer, state: { stateId: InterventionStatus.inCreation, stateLabel: '' } };
    },
    initIntervention: (_, action: PayloadAction<InterventionStoreState>) => action.payload,
    resetIntervention: (_, action: PayloadAction<boolean | undefined>) => ({ ...initialState, reload: Boolean(action.payload) }),
    addFromTraceability: state => ({
      ...state,
      fromTraceability: true,
    }),
    removeFromTraceability: state => ({
      ...state,
      fromTraceability: false,
    }),
    updateType: (state, action: PayloadAction<InterventionUpdateType>) => {
      state.interventionType = action.payload.interventionType;
    },
    updateBusinessType: (state, action: PayloadAction<BusinessType>) => {
      state.businessType = action.payload;
    },
    updateSparePartOrderNumbers: (state, action: PayloadAction<{ articleId: number; orderNumber: number }>) => {
      const { articleId, orderNumber } = action.payload;

      const editedNatures = state.natures.map(nature => ({
        ...nature,
        spareParts: nature.spareParts.map(sparePart => {
          const { orderNumbers, articles } = sparePart;

          const isOrderedArticle = articles.some(article => article.articleId === articleId);

          if (isOrderedArticle) {
            return {
              ...sparePart,
              articles: articles.map(a => ({ ...a, orderInProgress: a.articleId === articleId })),
              orderNumbers: [...(orderNumbers || []), orderNumber],
            };
          }

          return sparePart;
        }),
      }));

      return {
        ...state,
        natures: editedNatures,
      };
    },
    updateInterventionRef: (state, action: PayloadAction<string>) => {
      state.interventionRef = action.payload;
    },
    updateIsWarranty: (state, action: PayloadAction<boolean>) => {
      state.isWarranty = action.payload;
    },
    addOrUpdateInterventionNatures: (state, action: PayloadAction<AddOrUpdateInterventionNaturesPayload>) => {
      const { interventionNatures } = action.payload;

      const updatedNatures = interventionNatures.reduce((acc: InterventionNature[], nature) => {
        const existingNature = acc.some(stateNature => stateNature.natureId === nature.natureId);
        if (!existingNature) {
          return [...acc, nature];
        }
        return addNatureEntities(nature, acc);
      }, state.natures);

      state.natures = updatedNatures;
    },
    updatePecService: (state, action: PayloadAction<{ natureId: number; service: Entity }>) => {
      state.natures = updatePecEntity(state.natures, action.payload.natureId, EntityType.services, action.payload.service, true);
    },
    updatePecSparePart: (state, action: PayloadAction<{ natureId: number; sparePart: Entity }>) => {
      state.natures = updatePecEntity(state.natures, action.payload.natureId, EntityType.spareParts, action.payload.sparePart);
    },
    setOrderInProgress: (state, action: PayloadAction<{ articleId: number; progress: boolean }>) => {
      state.natures = state.natures.map(nature => {
        return {
          ...nature,
          spareParts: nature.spareParts.map(spare => {
            return {
              ...spare,
              articles: spare.articles.map(article => {
                if (article.articleId === action.payload.articleId) {
                  return { ...article, orderInProgress: action.payload.progress };
                }
                return article;
              }),
            };
          }),
        };
      });
    },
    updateStoreComment: (state, action: PayloadAction<string>) => {
      state.storeComment = action.payload;
    },
    updateWorkshopComment: (state, action: PayloadAction<string>) => {
      state.workshopComment = action.payload;
    },
    updateCreationSite: (state, action: PayloadAction<MasterDataStore>) => {
      state.creationSite = action.payload;
    },
    updateRealizationSite: (state, action: PayloadAction<RealizationSite>) => {
      state.realizationSite = action.payload;
    },
    updateFilesList: (state, action: PayloadAction<{ files?: FileList; names: string[] }>) => ({
      ...state,
      files: {
        ...state.files,
        ...(action.payload.files && { list: action.payload.files }),
        names: action.payload.names.filter((fileName, index) => action.payload.names.indexOf(fileName) === index),
      },
    }),
    updateFooter: (state, action: PayloadAction<FooterInfos>) => {
      state.footer = action.payload;
    },
    updateFooterState: (state, action: PayloadAction<InterventionState>) => {
      state.footer.state = { ...state.footer.state, ...action.payload };
    },
    setDisableActionButton: (state, action: PayloadAction<boolean>) => {
      state.disable.actionButton = action.payload;
    },
    setModalsDisplay: (state, action: PayloadAction<Partial<ModalsDisplay>>) => ({
      ...state,
      modalsDisplay: {
        ...state.modalsDisplay,
        ...action.payload,
      },
    }),
    setMissingFields: (state, action: PayloadAction<Partial<MissingFields>>) => ({
      ...state,
      missingFields: {
        ...state.missingFields,
        ...action.payload,
      },
    }),
    setInterventionToDelete: state => {
      state.isToDelete = true;
    },
    updateTraceabilitySource: (state, action: PayloadAction<TraceabilitySource>) => {
      state.traceabilitySource = action.payload;
    },
    setBatteryCheck: (state, action: PayloadAction<boolean>) => {
      state.isBatteryChecked = action.payload;
    },
    updateDepositedProduct: (state, action: PayloadAction<PatchInterventionProduct>) => {
      state.depositedProduct = { ...state.depositedProduct, ...action.payload };
    },
    updateBonusRepairCode: (state, action: PayloadAction<string | null>) => {
      state.bonusRepairCode = action.payload;
    },
  },
});

export const {
  reducer: interventionReducer,
  actions: {
    addOrUpdateInterventionNatures,
    initIntervention,
    initInterventionState,
    addFromTraceability,
    removeFromTraceability,
    resetIntervention,
    updateSparePartOrderNumbers,
    setOrderInProgress,
    updateCreationSite,
    updateFilesList,
    updateIsWarranty,
    updateFooter,
    updateFooterState,
    updateInterventionRef,
    updatePecService,
    updatePecSparePart,
    updateWorkshopComment,
    updateType,
    updateStoreComment,
    updateRealizationSite,
    setDisableActionButton,
    setModalsDisplay,
    setMissingFields,
    setInterventionToDelete,
    updateTraceabilitySource,
    setBatteryCheck,
    updateDepositedProduct,
    updateBonusRepairCode,
    updateBusinessType,
  },
} = interventionSlice;
